import { useUnit } from 'effector-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Panel, Separator, useDefaultLayout, usePanelRef } from 'react-resizable-panels';
import { muteToggled, soloToggled } from '../../entities/item-visibility';
import { $project, $readOnly, redo, undo } from '../../entities/project';
import {
  $focusedScenarioId,
  $inspector,
  $selectedItem,
  assumptionsOpened,
  inspectorClosed,
  itemSelected,
} from '../../entities/selection';
import {
  $view,
  simModeToggled,
  valueModeToggled,
  viewChanged,
  VIEWS,
  visibleViews,
  type View,
} from '../../entities/view-settings';
import { duplicateRequested, removeRequested } from '../../features/item-actions';
import { addVariantRequested } from '../../features/manage-variants';
import { TourCoachMark } from '../../features/onboarding';
import { useHotkeys } from '../../shared/lib/hotkeys';
import { safeLocalStorage } from '../../shared/lib/safe-storage';
import { COMPACT_QUERY, useMediaQuery } from '../../shared/lib/use-media-query';
import { VariantsDrawer } from './variants-drawer';
import { Tabs } from '../../shared/ui';
import { ChartCanvas } from '../../widgets/chart-canvas';
import { Inspector } from '../../widgets/inspector';
import { TopBar } from '../../widgets/top-bar';
import {
  $collapsed,
  addMenuOpened,
  stepRow,
  VariantsPanel,
  visibleRows,
} from '../../widgets/variants-panel';
import './model';

function isView(value: string): value is View {
  return (VIEWS as readonly string[]).includes(value);
}

const separatorClass =
  'w-px bg-border-subtle transition-colors outline-none hover:bg-accent focus-visible:bg-accent data-[separator=active]:bg-accent';

export function ProjectPage() {
  return <ProjectWorkspace />;
}

/**
 * The project layout: variants, charts and inspector (or, on small screens, a chart viewer).
 * Also shows shared projects (pages/shared), read-only: editing shortcuts are off.
 */
export function ProjectWorkspace() {
  const { t } = useTranslation();
  const readOnly = useUnit($readOnly);
  const [project, view, onView, toggleValueMode, inspector, selected, focusedId, collapsed] =
    useUnit([
      $project,
      $view,
      viewChanged,
      valueModeToggled,
      $inspector,
      $selectedItem,
      $focusedScenarioId,
      $collapsed,
    ]);
  const [
    select,
    closeInspector,
    openAssumptions,
    remove,
    duplicate,
    addVariant,
    openAddMenu,
    onUndo,
    onRedo,
  ] = useUnit([
    itemSelected,
    inspectorClosed,
    assumptionsOpened,
    removeRequested,
    duplicateRequested,
    addVariantRequested,
    addMenuOpened,
    undo,
    redo,
  ]);
  const [toggleMute, toggleSolo] = useUnit([muteToggled, soloToggled]);
  const variantsPanel = usePanelRef();
  const inspectorPanel = usePanelRef();
  const layout = useDefaultLayout({ id: 'finsim:layout:project:v2', storage: safeLocalStorage });
  const compact = useMediaQuery(COMPACT_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // The inspector panel follows the selection.
  useEffect(() => {
    const panel = inspectorPanel.current;
    if (!panel) return;
    if (inspector && panel.isCollapsed()) panel.expand();
    if (!inspector && !panel.isCollapsed()) panel.collapse();
  }, [inspector, inspectorPanel]);

  const toggleVariants = (): void => {
    const panel = variantsPanel.current;
    if (!panel) return;
    if (panel.isCollapsed()) panel.expand();
    else panel.collapse();
  };
  const toggleInspector = (): void => {
    if (inspector) closeInspector();
    else openAssumptions();
  };
  const move = (delta: 1 | -1) => () => {
    if (!project) return;
    const next = stepRow(visibleRows(project, collapsed), selected, delta);
    if (next) select(next);
  };

  const toggleSimMode = useUnit(simModeToggled);
  // Viewing shortcuts work everywhere; editing ones only on editable projects.
  const editBindings = {
    // Edit the selected item's main value (amount, balance, principal, percentage).
    e: selected
      ? () => {
          document.querySelector<HTMLInputElement>('.primary-field input')?.focus();
        }
      : undefined,
    Backspace: selected
      ? () => {
          remove(selected);
        }
      : undefined,
    Delete: selected
      ? () => {
          remove(selected);
        }
      : undefined,
    'mod+d': selected
      ? () => {
          const item = project?.items[selected.itemId];
          if (item)
            duplicate({ ...selected, name: t('itemActions.copyName', { name: item.name }) });
        }
      : undefined,
    c: focusedId
      ? () => {
          openAddMenu(focusedId);
        }
      : undefined,
    'shift+c': project
      ? () => {
          addVariant({ name: t('variants.defaultName', { n: project.scenarios.length + 1 }) });
        }
      : undefined,
    'mod+z': onUndo,
    'shift+mod+z': onRedo,
    'mod+y': onRedo,
  };
  useHotkeys({
    '[': toggleVariants,
    ']': toggleInspector,
    r: toggleValueMode,
    'g a': openAssumptions,
    'shift+m': toggleSimMode,
    j: move(1),
    k: move(-1),
    ArrowDown: move(1),
    ArrowUp: move(-1),
    Escape: inspector ? closeInspector : undefined,
    m: selected
      ? () => {
          toggleMute(selected);
        }
      : undefined,
    s: selected
      ? () => {
          toggleSolo(selected);
        }
      : undefined,
    ...Object.fromEntries(
      visibleViews(project).map((v, i) => [
        String(i + 1),
        () => {
          onView(v);
        },
      ]),
    ),
    ...(readOnly ? {} : editBindings),
  });

  // Small screens: a read-only chart viewer, with the variants in a drawer (docs/PLAN.md §1).
  if (compact) {
    return (
      <Tabs
        value={view}
        onValueChange={(value) => {
          if (isView(value)) onView(value);
        }}
        className="flex h-full flex-col"
      >
        <TopBar
          compact
          onToggleVariants={() => {
            setDrawerOpen(true);
          }}
          onToggleInspector={() => undefined}
        />
        <p
          role="note"
          className="shrink-0 border-b border-border-subtle bg-bg-1 px-3 py-1.5 text-xs text-text-3"
        >
          {readOnly ? t('viewer.viewOnlyHint') : t('compact.readOnly')}
        </p>
        <div className="min-h-0 flex-1">
          <ChartCanvas />
        </div>
        <VariantsDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
      </Tabs>
    );
  }

  return (
    <Tabs
      value={view}
      onValueChange={(value) => {
        if (isView(value)) onView(value);
      }}
      className="flex h-full flex-col"
    >
      <TopBar onToggleVariants={toggleVariants} onToggleInspector={toggleInspector} />
      <Group orientation="horizontal" className="min-h-0 flex-1" {...layout}>
        <Panel
          id="variants"
          panelRef={variantsPanel}
          defaultSize={280}
          minSize={220}
          maxSize={460}
          collapsible
        >
          <VariantsPanel />
        </Panel>
        <Separator aria-label={t('panels.resize')} className={separatorClass} />
        <Panel id="canvas" minSize={420}>
          <ChartCanvas />
        </Panel>
        <Separator
          aria-label={t('panels.resize')}
          className={separatorClass}
          disabled={!inspector}
        />
        <Panel
          id="inspector"
          panelRef={inspectorPanel}
          defaultSize={360}
          minSize={300}
          maxSize={560}
          collapsible
          collapsedSize={0}
        >
          <Inspector />
        </Panel>
      </Group>
      {!readOnly && <TourCoachMark />}
    </Tabs>
  );
}
